import React from "react";
import LoginNav from "./components/LoginNav";
import {
  FileText,
  Layers,
  BarChart2,
  Mail,
  Zap,
  BookOpen,
  Search,
  CheckCircle,
} from "lucide-react";

const Tag = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      background: "#f0efff",
      color: "#402c7a",
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.05em",
      padding: "0.25rem 0.75rem",
      borderRadius: "999px",
      border: "1px solid #5a3792",
      marginBottom: "1rem",
    }}
  >
    {children}
  </span>
);

const FeatureSection = ({
  tag,
  title,
  description,
  bullets,
  imgSrc,
  imgAlt,
  reverse,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: reverse ? "row-reverse" : "row",
      alignItems: "center",
      gap: "4rem",
      padding: "5rem 2rem",
      maxWidth: "72rem",
      margin: "0 auto",
    }}
    className="features-section-responsive"
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <Tag>{tag}</Tag>
      <h2
        style={{
          fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
          fontWeight: 800,
          color: "#111827",
          lineHeight: 1.2,
          marginBottom: "1rem",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          color: "#6b7280",
          lineHeight: 1.7,
          marginBottom: "1.5rem",
          fontSize: "1.05rem",
        }}
      >
        {description}
      </p>
      {bullets && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: "0.6rem",
          }}
        >
          {bullets.map((b, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                color: "#374151",
                fontSize: "0.95rem",
              }}
            >
              <CheckCircle
                size={16}
                style={{ color: "#402c7a", flexShrink: 0 }}
              />
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>

    <div
      style={{
        flex: 1,
        minWidth: 0,
        borderRadius: "16px",
        overflow: "hidden",
        background: "#eef2ff",
        boxShadow: "0 20px 50px rgba(79,70,229,0.12)",
        aspectRatio: "16/10",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={imgAlt}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span style={{ color: "#a5b4fc", fontSize: "0.85rem" }}>{imgAlt}</span>
      )}
    </div>
  </div>
);

const MiniCard = ({ icon: Icon, title, description }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #f0efff",
      borderRadius: "12px",
      padding: "1.5rem",
      transition: "box-shadow 0.15s, transform 0.15s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = "0 8px 24px rgba(79,70,229,0.1)";
      e.currentTarget.style.transform = "translateY(-2px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.transform = "none";
    }}
  >
    <div
      style={{
        width: "2.25rem",
        height: "2.25rem",
        borderRadius: "8px",
        background: "#f0efff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "0.75rem",
      }}
    >
      <Icon size={18} style={{ color: "#402c7a" }} />
    </div>
    <p
      style={{
        fontWeight: 700,
        color: "#111827",
        marginBottom: "0.35rem",
        fontSize: "0.95rem",
      }}
    >
      {title}
    </p>
    <p style={{ color: "#6b7280", fontSize: "0.85rem", lineHeight: 1.6 }}>
      {description}
    </p>
  </div>
);

const MINI_CARDS = [
  {
    icon: Mail,
    title: "Cover letters",
    description:
      "Generate targeted, human-sounding cover letters grounded in your actual resume.",
  },
  {
    icon: BookOpen,
    title: "Skill gap analysis",
    description:
      "Visualize which skills your saved jobs require and where your resume falls short.",
  },
  {
    icon: Zap,
    title: "ATS match scores",
    description:
      "Every saved job is scored against your resume so you apply where you match best.",
  },
  {
    icon: BarChart2,
    title: "Application insights",
    description:
      "See your application patterns, outcomes, and response rates at a glance.",
  },
  {
    icon: FileText,
    title: "Profile builder",
    description:
      "Build a rich profile once — contact info, education, work history — reused everywhere.",
  },
];

const FeaturesScreen = ({ navigateTo }) => (
  <div
    style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "inherit" }}
  >
    <LoginNav navigateTo={navigateTo} currentScreen="features" />

    {/* Hero */}
    <section
      style={{
        paddingTop: "8rem",
        paddingBottom: "5rem",
        textAlign: "center",
        padding: "8rem 1.5rem 5rem",
      }}
    >
      <Tag>Job search, reinvented</Tag>
      <h1
        style={{
          fontSize: "clamp(2.2rem, 6vw, 3.5rem)",
          fontWeight: 800,
          color: "#111827",
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
          marginBottom: "1.25rem",
        }}
        className="applicant-theme-text"
      >
        Your Job Search,
        <br />
        <span style={{ color: "#402c7a" }}>Streamlined.</span>
      </h1>
      <p
        style={{
          color: "#6b7280",
          fontSize: "1.15rem",
          lineHeight: 1.65,
          maxWidth: "36rem",
          margin: "0 auto 2.5rem",
        }}
      >
        ApplicantHQ handles the busywork - tailoring your resume to every job,
        tracking every application, and showing you exactly where to focus.
      </p>
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => navigateTo("login")}
          className="buttons-indigo"
          style={{
            padding: "0.7rem 1.25rem",
            borderRadius: "999px",
            border: "none",
            background: "linear-gradient(135deg, #402c7a, #826dc0)",
            fontWeight: 700,
            fontSize: "0.9rem",
            marginBottom: "1.5rem",
            transition: "opacity 0.15s",
          }}
        >
          Get started free
        </button>
        <button
          onClick={() => navigateTo("help")}
          className="buttons-navigation"
          style={{
            padding: "0.7rem 1.25rem",
            borderRadius: "999px",
            background: "transparent",
            border: "1px solid #d1d5db",
            color: "#374151",
            fontWeight: 700,
            fontSize: "0.9rem",
            marginBottom: "1.5rem",
            transition: "opacity 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#402c7a")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#d1d5db")}
        >
          Learn more →
        </button>
      </div>
    </section>

    {/* Divider */}
    <div
      style={{
        borderTop: "1px solid #e5e7eb",
        maxWidth: "72rem",
        margin: "0 auto",
      }}
    />

    {/* Feature sections */}
    <FeatureSection
      tag="AI-Powered"
      title="Tailor your resume to every job"
      description="Paste a job description and ApplicantHQ rewrites your resume to match — highlighting relevant experience, rephrasing bullets, and aligning your skills with what the employer wants."
      bullets={[
        "ATS score before and after tailoring",
        "Summary of every change made",
        "Download as PDF instantly",
      ]}
      imgSrc="/assets/screenshots/batch-tailoring.png"
      imgAlt="Resume tailoring screenshot"
    />

    <div style={{ background: "#fff" }}>
      <FeatureSection
        tag="Save Time"
        title="Apply to multiple jobs at once"
        description="Select a batch of saved jobs and tailor your resume to all of them in one click. ApplicantHQ queues each one, downloads the results, and tells you exactly how many succeeded."
        bullets={[
          "Batch up to your plan limit at once",
          "Per-job success and failure reporting",
          "Saves back to your application history",
        ]}
        imgSrc="/assets/screenshots/application-tracking.png"
        imgAlt="Batch tailoring screenshot"
        reverse
      />
    </div>

    <FeatureSection
      tag="Stay Organized"
      title="Track every application in one place"
      description="Log outcomes, add notes, and sort by status. See your full job search history at a glance — no more spreadsheets, no more lost follow-ups."
      bullets={[
        "Status tracking: applied, interviewing, offer, rejected",
        "Match score per application",
        "Sortable by date, company, or outcome",
      ]}
      imgSrc="/assets/screenshots/visualizations.png"
      imgAlt="Application tracking screenshot"
    />

    {/* Mini feature cards */}
    <div style={{ background: "#fff", padding: "5rem 1.5rem" }}>
      <div style={{ maxWidth: "72rem", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "3rem" }}>
          <Tag>Everything else</Tag>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 800,
              color: "#111827",
              marginTop: "0.5rem",
            }}
          >
            More tools, one platform
          </h2>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: "1.25rem",
          }}
        >
          {MINI_CARDS.map((c) => (
            <MiniCard
              key={c.title}
              icon={c.icon}
              title={c.title}
              description={c.description}
            />
          ))}
        </div>
      </div>
    </div>

    {/* CTA */}
    <section
      style={{
        background:
          "linear-gradient(135deg, #402c7a 0%, rgb(130, 109, 192) 100%)",
        padding: "5rem 1.5rem",
        textAlign: "center",
        color: "#fff",
      }}
    >
      <h2
        style={{
          fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
          fontWeight: 800,
          marginBottom: "1rem",
        }}
      >
        Ready to take your job search further?
      </h2>
      <p
        style={{ color: "#c7d2fe", marginBottom: "2rem", fontSize: "1.05rem" }}
      >
        Start free. No credit card required.
      </p>
      <button
        onClick={() => navigateTo("login")}
        style={{
          background: "#fff",
          color: "#402c7a",
          border: "none",
          borderRadius: "8px",
          padding: "0.75rem 2rem",
          fontSize: "1rem",
          fontWeight: 700,
          cursor: "pointer",
          transition: "opacity 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        Get started free
      </button>
    </section>
  </div>
);

export default FeaturesScreen;
